import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { HeimvorteilGebaeudeart, HeimvorteilProduktart } from '../../../../shared/enum';
import { toggleEditMode } from '../../../../shared/utils';
import { IHvErweiterungen } from '../../../models/hverweiterungen';
import { DateService } from '../../../service/date.service';
import { PopupService } from '../../../service/popup.service';
import { AufbewahrungWertsachenComponent } from '../../aufbewahrung-wertsachen/aufbewahrung-wertsachen.component';

@Component({
  selector: 'app-calculatehv-erweiterungen',
  templateUrl: './calculatehv-erweiterungen.component.html',
  styleUrls: ['./calculatehv-erweiterungen.component.scss']
})
export class CalculateHvErweiterungenComponent implements OnInit, OnChanges {

  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  produktart: HeimvorteilProduktart;

  @Input()
  gebaeudeart: HeimvorteilGebaeudeart;

  @Input()
  expertMode: boolean;

  @Output()
  calculateRatePendingEvent = new EventEmitter<void>();

  @ViewChild(AufbewahrungWertsachenComponent) aufbewahrungWertsachenComponent: AufbewahrungWertsachenComponent;

  constructor(private fb: UntypedFormBuilder, public dateService: DateService, public popupService: PopupService,
    public translateService: TranslateService, private ref: ChangeDetectorRef) { }

  static createFormGroup(fb: UntypedFormBuilder) {
    return fb.group({
      erhoehungVersicherungssummeHaushalt: new UntypedFormControl('', Validators.required),
      erhoehungVersicherungssummeEigenheim: new UntypedFormControl('', Validators.required),
      haftpflichtHunde: new UntypedFormControl('', Validators.required),
      haftpflichtAnzahlHunde: new UntypedFormControl('', Validators.required),
      haftpflichtWeiterePersonen: new UntypedFormControl('', Validators.required),
      haftpflichtAnzahlWeiterePersonen: new UntypedFormControl('', Validators.required),
      selbstbehalt: new UntypedFormControl('', Validators.required),
      sicherheitsEingangstuer: new UntypedFormControl('', Validators.required),
      alarmanlage: new UntypedFormControl('', Validators.required),
      aufbewahrungWertsachen: AufbewahrungWertsachenComponent.createFormGroup(fb)
    });
  }

  ngOnInit(): void {
    this.onValueChanged();
    this.toggleEditMode();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.produktart && changes.produktart.currentValue !== changes.produktart.previousValue) {
      this.toggleEditMode();
    }
  }

  get erhoehungVersicherungssummeHaushalt() { return this.formGroup.get('erhoehungVersicherungssummeHaushalt'); }
  get erhoehungVersicherungssummeEigenheim() { return this.formGroup.get('erhoehungVersicherungssummeEigenheim'); }
  get haftpflichtHunde() { return this.formGroup.get('haftpflichtHunde'); }
  get haftpflichtAnzahlHunde() { return this.formGroup.get('haftpflichtAnzahlHunde'); }
  get haftpflichtWeiterePersonen() { return this.formGroup.get('haftpflichtWeiterePersonen'); }
  get haftpflichtAnzahlWeiterePersonen() { return this.formGroup.get('haftpflichtAnzahlWeiterePersonen'); }
  get selbstbehalt() { return this.formGroup.get('selbstbehalt'); }
  get sicherheitsEingangstuer() { return this.formGroup.get('sicherheitsEingangstuer'); }
  get alarmanlage() { return this.formGroup.get('alarmanlage'); }
  get aufbewahrungWertsachen() { return this.formGroup.get('aufbewahrungWertsachen'); }

  get showErhoehungVersicherungssummeHaushalt() {
    return this.produktart === HeimvorteilProduktart.Haushalt ||
      this.produktart === HeimvorteilProduktart.Buendel ? true : false;
  }
  get showErhoehungVersicherungssummeEigenheim() {
    return this.produktart === HeimvorteilProduktart.Eigenheim ||
      this.produktart === HeimvorteilProduktart.Buendel ? true : false;
  }

  get showHaftpflichtAnzahlWeiterePersonen() {
    return this.haftpflichtWeiterePersonen?.value === 'ja' ? true : false;
  }

  get showProduktHaushalt() {
    return this.produktart === HeimvorteilProduktart.Haushalt ||
      this.produktart === HeimvorteilProduktart.Buendel ? true : false;
  }
  get showProduktEigenheim() {
    return this.produktart === HeimvorteilProduktart.Eigenheim ||
      this.produktart === HeimvorteilProduktart.Buendel ? true : false;
  }
  get showProduktBuendel() {
    return this.produktart === HeimvorteilProduktart.Buendel ? true : false;
  }

  get showHaftpflichtAnzahlHunde() {
    return this.haftpflichtHunde.value === 'ja' ? true : false;
  }

  onValueChanged(): void {
    this.haftpflichtHunde.valueChanges.subscribe(() => {
      this.toggleEditMode();
      if (this.haftpflichtHunde.value === 'ja') {
        this.haftpflichtAnzahlHunde.setValue(1);
      }
    });

    this.haftpflichtWeiterePersonen.valueChanges.subscribe(() => {
      this.toggleEditMode();
      if (this.haftpflichtWeiterePersonen.value === 'ja') {
        this.haftpflichtAnzahlWeiterePersonen.setValue(1);
      }
    });

    merge(
      this.erhoehungVersicherungssummeHaushalt.valueChanges,
      this.erhoehungVersicherungssummeEigenheim.valueChanges,
      this.selbstbehalt.valueChanges,
      this.sicherheitsEingangstuer.valueChanges,
      this.alarmanlage.valueChanges,
      this.haftpflichtHunde.valueChanges,
      this.haftpflichtAnzahlHunde.valueChanges,
      this.haftpflichtWeiterePersonen.valueChanges,
      this.haftpflichtAnzahlWeiterePersonen.valueChanges
    ).subscribe(() => {
      this.calculateRatePendingEvent.emit();
    });
  }

  toggleEditMode(): void {
    toggleEditMode(this.erhoehungVersicherungssummeHaushalt, this.showErhoehungVersicherungssummeHaushalt);
    toggleEditMode(this.erhoehungVersicherungssummeEigenheim, this.showErhoehungVersicherungssummeEigenheim);
    toggleEditMode(this.haftpflichtAnzahlHunde, this.showHaftpflichtAnzahlHunde);
    toggleEditMode(this.haftpflichtWeiterePersonen, this.showProduktHaushalt);
    toggleEditMode(this.haftpflichtAnzahlWeiterePersonen, this.showHaftpflichtAnzahlWeiterePersonen);
    toggleEditMode(this.selbstbehalt, this.showProduktHaushalt);
    toggleEditMode(this.sicherheitsEingangstuer, this.showProduktHaushalt);
    toggleEditMode(this.alarmanlage, this.showProduktHaushalt);
  }

  onCalculateRatePending() {
    this.calculateRatePendingEvent.emit();
  }

  getErweiterungen(): IHvErweiterungen {
    return {
      ErhoehungVersicherungssummeHaushalt: this.erhoehungVersicherungssummeHaushalt.value === 'ja' ? true : false,
      ErhoehungVersicherungssummeEigenheim: this.erhoehungVersicherungssummeEigenheim.value === 'ja' ? true : false,
      Selbstbehalt: this.selbstbehalt.value === 'ja' ? true : false,
      SicherheitsEingangstuer: this.sicherheitsEingangstuer.value ? this.sicherheitsEingangstuer.value === 'ja' ? true : false : null,
      Alarmanlage: this.alarmanlage.value ? this.alarmanlage.value === 'ja' ? true : false : null,
      ...this.aufbewahrungWertsachenComponent.getAufbewahrungWertsachen(),
      HaftpflichtAnzahlWeiterePersonen: this.haftpflichtAnzahlWeiterePersonen.value ? Number(this.haftpflichtAnzahlWeiterePersonen.value) : null,
      HaftpflichtAnzahlHunde: this.haftpflichtAnzahlHunde.value ? Number(this.haftpflichtAnzahlHunde.value) : null
    };
  }

  setErweiterungen(erweiterungen: IHvErweiterungen): void {
    this.erhoehungVersicherungssummeHaushalt.setValue(erweiterungen.ErhoehungVersicherungssummeHaushalt? 'ja': 'nein');
    this.erhoehungVersicherungssummeEigenheim.setValue(erweiterungen.ErhoehungVersicherungssummeEigenheim? 'ja': 'nein');
    this.selbstbehalt.setValue(erweiterungen.Selbstbehalt? 'ja': 'nein');
    this.sicherheitsEingangstuer.setValue(erweiterungen.SicherheitsEingangstuer === null? null: erweiterungen.SicherheitsEingangstuer? 'ja': 'nein');
    this.alarmanlage.setValue(erweiterungen.Alarmanlage === null? null: erweiterungen.SicherheitsEingangstuer? 'ja': 'nein');
    this.aufbewahrungWertsachenComponent.setAufbewahrungWertsachen(erweiterungen);
    this.haftpflichtHunde.setValue(erweiterungen.HaftpflichtAnzahlHunde > 0? 'ja': 'nein');
    this.haftpflichtAnzahlHunde.setValue(erweiterungen.HaftpflichtAnzahlHunde);
    this.haftpflichtWeiterePersonen.setValue(erweiterungen.HaftpflichtAnzahlWeiterePersonen > 0? 'ja': 'nein');
    this.haftpflichtAnzahlWeiterePersonen.setValue(erweiterungen.HaftpflichtAnzahlWeiterePersonen);
    this.ref.detectChanges();
  }
}
